import type { Role } from "./schemas/tenant";

// Team import: a payroll or HR export (any provider, any column names) →
// Clevis people. Every column is read and profiled (its header AND its
// values), each gets a predicted Clevis field with the reason, and titles and
// department names are matched to roles and Clevis departments. Pure logic,
// shared by the app (instant predictions) and the tests. Columns that look
// like SSNs, bank details or birth dates are recognized and never imported.

export const TEAM_FIELDS = [
  { id: "fullName", label: "Full name" },
  { id: "firstName", label: "First name" },
  { id: "lastName", label: "Last name" },
  { id: "email", label: "Email" },
  { id: "phone", label: "Phone" },
  { id: "address", label: "Home address" },
  { id: "address2", label: "Address line 2" },
  { id: "city", label: "City" },
  { id: "state", label: "State" },
  { id: "zip", label: "ZIP" },
  { id: "title", label: "Job title" },
  { id: "department", label: "Department" },
  { id: "role", label: "Clevis role" },
  { id: "hourlyRate", label: "Pay rate" },
  { id: "payrollId", label: "Employee ID" },
  { id: "status", label: "Employment status" },
  { id: "ignore", label: "Don't import" },
  { id: "sensitive", label: "Private (never imported)" },
] as const;
export type TeamFieldId = (typeof TEAM_FIELDS)[number]["id"];
/** Fields a column can be mapped to by a person (sensitive is detection-only). */
export const MAPPABLE_FIELDS: TeamFieldId[] = TEAM_FIELDS.map((f) => f.id).filter((f) => f !== "sensitive");

const HEADERS: Array<[TeamFieldId, RegExp]> = [
  ["sensitive", /\b(ssn|social\s*sec|sin\b|tax\s*id|tin\b|bank|routing|aba\b|account\s*(no|num|#)|acct|iban|swift|birth|dob\b|date\s*of\s*birth|garnish|salary\s*history|medical|i-?9|visa|passport|license\s*(no|num|#)|dl\s*#?|sex|gender|ethnic|race|marital|religio|disabilit|veteran|citizen)\b/i],
  ["ignore", /pay(ment)?\s*(unit|type|frequency|period|basis|schedule)|flsa|exempt|^unit$|address\s*(type|3)|^county$|^country$|(email|phone)\s*type|extension$|^ext\.?$/i],
  // Home address, one column each (kept private, like pay).
  ["address2", /^(home\s*|mailing\s*|street\s*|residential\s*)?addr(ess)?\s*(line\s*)?2$|^(apt|suite|unit)\b/i],
  ["address", /^(home\s*|mailing\s*|street\s*|residential\s*)?addr(ess)?(\s*(line\s*)?1)?$|^street(\s*address)?$/i],
  ["city", /^(home\s*|mailing\s*)?city$|^town$/i],
  ["state", /^(home\s*|mailing\s*)?state(\s*\/?\s*(province|region))?$|^province$/i],
  ["zip", /^(home\s*|mailing\s*)?(zip|postal)(\s*code)?$|^postcode$/i],
  ["email", /e-?mail/i],
  ["phone", /phone|mobile|cell|tel\b|contact\s*(no|num|#)/i],
  ["firstName", /^(legal\s*)?first(\s*name)?$|^given(\s*name)?$|^fname$|^preferred\s*first/i],
  ["lastName", /^(legal\s*)?last(\s*name)?$|^surname$|^family(\s*name)?$|^lname$/i],
  ["fullName", /^(employee|worker|team\s*member|staff)?\s*(full\s*)?name$|^name$|^employee$|^worker$/i],
  ["payrollId", /(employee|emp|worker|staff|file|payroll|badge|associate)\.?\s*(id|#|no\.?|num|number|code)\b|^id$|^file\s*#$/i],
  ["title", /title|position|job(\s*(name|role|title))?$|occupation|classification/i],
  ["department", /dep(t|artment)|division|team$|crew|branch|cost\s*center|home\s*department/i],
  ["role", /^(clevis\s*)?role$|access(\s*level)?|permission/i],
  ["hourlyRate", /rate|wage|hourly|pay(\s*rate)?$|salary|compensation|base\s*pay/i],
  ["status", /status|active|employment|terminat|separation/i],
];

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE = /^\+?1?[\s.-]?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}(\s*(x|ext\.?)\s*\d+)?$/i;
const SSN = /^\d{3}-?\d{2}-?\d{4}$/;
const MONEY = /^\$?\s*\d{1,3}(,?\d{3})*(\.\d{1,4})?\s*(\/\s*(hr|hour|h|yr|year|wk|week))?$/i;
const STATUS_WORDS = /^(active|inactive|terminated|term|termed|on\s*leave|leave|seasonal|part[-\s]?time|full[-\s]?time|a|t|i|l|rehire|separated|retired|deceased|suspended)$/i;
const NAME_PART = /^[A-Za-zÀ-ÿ'’.-]{1,30}$/;
/** Words that show up in job titles, not in names or department names. */
const TITLE_WORD = /\b(tech(nician)?|install(er)?|helper|apprentice|manager|supervisor|dispatch(er)?|csr|coordinator|estimator|sales(person)?|rep(resentative)?|driver|foreman|lead|assistant|clerk|bookkeeper|accountant|owner|president|admin(istrator)?|receptionist|laborer|mechanic|warehouse|specialist|associate|engineer|director|officer|controller|analyst)\b/i;

export interface ColumnGuess {
  index: number;
  header: string;
  field: TeamFieldId;
  /** high = header and values agree; medium = one of them; low = a hunch. */
  confidence: "high" | "medium" | "low";
  why: string;
  samples: string[];
}

const ratio = (vals: string[], test: (v: string) => boolean) => (vals.length ? vals.filter(test).length / vals.length : 0);

/** What a column's values look like. */
export function profileValues(values: string[]) {
  const vals = values.map((v) => v.trim()).filter(Boolean);
  const distinct = new Set(vals.map((v) => v.toLowerCase()));
  const nums = vals.map((v) => parseFloat(v.replace(/[$,\s]/g, ""))).filter((n) => Number.isFinite(n));
  const twoWordNames = ratio(vals, (v) => {
    const parts = v.replace(/,/g, " ").split(/\s+/).filter(Boolean);
    return parts.length >= 2 && parts.length <= 4 && parts.every((p) => NAME_PART.test(p)) && /^[A-ZÀ-Ý]/.test(parts[0]!);
  });
  return {
    filled: vals.length,
    distinct: distinct.size,
    email: ratio(vals, (v) => EMAIL.test(v)),
    phone: ratio(vals, (v) => PHONE.test(v) && !SSN.test(v)),
    ssn: ratio(vals, (v) => SSN.test(v) && /-/.test(v)),
    money: ratio(vals, (v) => MONEY.test(v)),
    status: ratio(vals, (v) => STATUS_WORDS.test(v)),
    oneWord: ratio(vals, (v) => NAME_PART.test(v)),
    twoWordNames,
    titleLike: ratio(vals, (v) => TITLE_WORD.test(v)),
    date: ratio(vals, (v) => /^\d{1,4}[/-]\d{1,2}[/-]\d{1,4}$/.test(v)),
    addressLike: ratio(vals, (v) => /^\d{1,6}[A-Za-z]?\s+[A-Za-z0-9.'-]+(\s|$)/.test(v) && !/@/.test(v)),
    zip: ratio(vals, (v) => /^\d{5}(-\d{4})?$/.test(v)),
    medianNumber: nums.length ? nums.sort((a, b) => a - b)[Math.floor(nums.length / 2)]! : null,
  };
}

/**
 * Predict every column's field. Header wins when the values agree; values
 * decide when the header is unknown ("Column 7"). Each field is used once
 * (first/last name may pair); duplicates fall back to "Don't import".
 */
export function guessColumns(headers: string[], rows: string[][]): ColumnGuess[] {
  const guesses: ColumnGuess[] = headers.map((rawHeader, index) => {
    const header = rawHeader.trim();
    const values = rows.map((r) => r[index] ?? "");
    const p = profileValues(values);
    const samples = [...new Set(values.map((v) => v.trim()).filter(Boolean))].slice(0, 4);
    const byHeader = HEADERS.find(([, re]) => re.test(header))?.[0] ?? null;
    const say = (field: TeamFieldId, confidence: ColumnGuess["confidence"], why: string): ColumnGuess => ({ index, header, field, confidence, why, samples });

    if (byHeader === "sensitive" || p.ssn > 0.5) return say("sensitive", "high", "Looks like private data (SSN, bank, birth date); left out on purpose.");
    if (p.filled === 0) return say("ignore", "high", "Empty column.");
    if (byHeader === "ignore") return say("ignore", "high", /pay|flsa|exempt|unit/i.test(header) ? "Pay unit or type; not needed (rates are read as hourly or yearly on their own)." : "Not needed.");
    if (byHeader === "address" || byHeader === "address2" || byHeader === "city" || byHeader === "state" || byHeader === "zip") {
      return say(byHeader, "high", "Part of the person's home address (kept private, like pay).");
    }
    if (p.email > 0.6) return say("email", byHeader === "email" ? "high" : "medium", byHeader === "email" ? "Header and values are email addresses." : "The values are email addresses.");
    if (p.phone > 0.6) return say("phone", byHeader === "phone" ? "high" : "medium", "The values are phone numbers.");
    if (byHeader === "hourlyRate" || (p.money > 0.7 && p.medianNumber !== null && p.medianNumber > 5)) {
      const hourly = p.medianNumber !== null && p.medianNumber < 500;
      if (byHeader === "hourlyRate" || hourly) {
        return say("hourlyRate", byHeader === "hourlyRate" && p.money > 0.5 ? "high" : "medium", hourly ? "Dollar amounts that look hourly." : "Pay amounts (yearly ones are turned into hourly at 2,080 hours).");
      }
    }
    if (byHeader === "status" || p.status > 0.7) return say("status", byHeader === "status" && p.status > 0.4 ? "high" : "medium", "Active / terminated values.");
    if (byHeader === "firstName" || byHeader === "lastName") return say(byHeader, "high", `The header says ${byHeader === "firstName" ? "first" : "last"} name.`);
    if (byHeader === "payrollId") return say("payrollId", "high", "Employee IDs: kept so a later re-import updates the same people.");
    if (byHeader === "title") return say("title", "high", "Job titles: used to suggest each person's Clevis role.");
    if (byHeader === "department") return say("department", "high", "Matched to your Clevis departments.");
    if (byHeader === "role") return say("role", "medium", "Roles: matched to Clevis roles.");
    if (byHeader === "fullName" || (p.twoWordNames > 0.7 && p.titleLike < 0.3 && p.distinct > rows.length * 0.8)) {
      return say("fullName", byHeader === "fullName" ? "high" : "medium", p.twoWordNames > 0.5 ? "Values look like people's names." : "The header says name.");
    }
    if (p.date > 0.7) return say("ignore", "medium", "Dates (hire or birth dates aren't used).");
    if (p.addressLike > 0.7) return say("address", "medium", "Values look like street addresses.");
    if (p.zip > 0.8 && p.distinct < p.filled) return say("zip", "medium", "Values look like ZIP codes.");
    // Unknown header: values that repeat across people are titles (job words) or departments.
    const repeats = p.distinct > 0 && p.distinct < p.filled && p.distinct <= Math.max(3, rows.length * 0.6);
    if (p.titleLike > 0.5 && (repeats || p.distinct < p.filled)) return say("title", "medium", "Values look like job titles.");
    if (repeats) return say("department", "low", "Values repeat across people; looks like departments or teams.");
    if (p.distinct === p.filled && /^[A-Za-z]*\d+[A-Za-z\d-]*$/.test(samples[0] ?? "")) return say("payrollId", "low", "Unique codes; maybe employee IDs.");
    return say("ignore", "low", "Not sure what this is.");
  });

  // One column per field (first/last name can pair; a full name beats them).
  const rank = { high: 3, medium: 2, low: 1 } as const;
  const used = new Map<TeamFieldId, ColumnGuess>();
  // Among several phone columns, the mobile number is the one worth having.
  const pref = (g: ColumnGuess) => (g.field === "phone" && /mobile|cell/i.test(g.header) ? 1 : 0);
  for (const g of [...guesses].sort((a, b) => rank[b.confidence] - rank[a.confidence] || pref(b) - pref(a))) {
    if (g.field === "ignore" || g.field === "sensitive") continue;
    const had = used.get(g.field);
    if (had) {
      g.field = g.field === "title" && !used.has("department") ? "department" : "ignore";
      g.why = g.field === "department" ? "A second column of repeating values; likely departments." : `Another column already looks like ${had.header}.`;
      g.confidence = "low";
      if (g.field === "department") used.set("department", g);
      continue;
    }
    used.set(g.field, g);
  }
  return guesses;
}

/** "Doe, Jane" / "Jane Q. Doe" → display name "Jane Doe" style. */
export function personName(full: string, first = "", last = ""): string {
  const f = first.trim();
  const l = last.trim();
  if (f || l) return `${f} ${l}`.trim().replace(/\s+/g, " ");
  const n = full.trim().replace(/\s+/g, " ");
  if (/^[^,]+,\s*[^,]+$/.test(n)) {
    const [lastPart, firstPart] = n.split(/,\s*/);
    return `${firstPart} ${lastPart}`.trim();
  }
  return n;
}
const titleCase = (s: string) => (s === s.toUpperCase() || s === s.toLowerCase() ? s.toLowerCase().replace(/(^|[\s'’-])\p{L}/gu, (m) => m.toUpperCase()) : s);
export const tidyName = (s: string) => titleCase(s.trim());

export function normalizePhone(v: string): string {
  const d = v.replace(/\D/g, "");
  const ten = d.length === 11 && d.startsWith("1") ? d.slice(1) : d;
  return ten.length === 10 ? `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}` : v.trim();
}

/** "$23.50", "23.5/hr" → 2350 hourly; "$52,000" or "/yr" → hourly at 2,080 hours. Null when not a pay amount. */
export function hourlyCents(v: string): number | null {
  const s = v.trim().toLowerCase();
  if (!s) return null;
  const n = parseFloat(s.replace(/[^0-9.]/g, ""));
  if (!Number.isFinite(n) || n <= 0) return null;
  const yearly = /yr|year|annual|salary/.test(s) || n >= 1000;
  const weekly = /wk|week/.test(s);
  const hourly = yearly ? n / 2080 : weekly ? n / 40 : n;
  return hourly > 0 && hourly < 1000 ? Math.round(hourly * 100) : null;
}

/** Still working there? Unknown values count as active. */
export function isActiveStatus(v: string): boolean {
  return !/^(t|i|inactive|term|termed|terminated|separated|retired|deceased|former|quit|fired|resigned)$/i.test(v.trim());
}

/** A Clevis role from a job title (or a role column). */
export function roleFromTitle(title: string): Exclude<Role, "owner"> {
  const t = title.toLowerCase();
  if (!t.trim()) return "staff";
  if (/\b(owner|president|ceo|coo|cfo|vp|vice\s*president|general\s*manager|gm|principal|partner|admin(istrator)?)\b/.test(t)) return "admin";
  if (/\b(manager|supervisor|superintendent|director|lead\s*(dispatcher|coordinator)|operations)\b/.test(t)) return "manager";
  if (/\b(tech(nician)?|install(er)?|service|field|helper|apprentice|journeyman|laborer|labourer|driver|crew|foreman|mechanic|electrician|plumber|welder|carpenter|lead\s*installer|warehouse|yard)\b/.test(t)) return "tech";
  return "staff";
}

const norm = (s: string) => s.toLowerCase().replace(/&/g, " and ").replace(/[^a-z0-9 ]/g, " ").replace(/\s+/g, " ").trim();
const STOP = new Set(["and", "the", "dept", "department", "division", "team", "of", "s"]);
const tokens = (s: string) => norm(s).split(" ").filter((w) => w && !STOP.has(w)).map((w) => w.replace(/s$/, ""));

/** The Clevis department a payroll department name means, or null. */
export function matchDepartment(value: string, departments: Array<{ id: string; name: string }>): string | null {
  const v = norm(value);
  if (!v) return null;
  const exact = departments.find((d) => norm(d.name) === v);
  if (exact) return exact.id;
  const vt = tokens(value);
  let best: { id: string; score: number } | null = null;
  for (const d of departments) {
    const dt = tokens(d.name);
    if (!dt.length || !vt.length) continue;
    const hit = vt.filter((w) => dt.some((x) => x === w || (w.length > 3 && (x.startsWith(w) || w.startsWith(x))))).length;
    // Mostly how much of the payroll name is found; a closer overall fit breaks ties.
    const score = hit / vt.length + hit / (dt.length * 10);
    if (hit > 0 && (!best || score > best.score)) best = { id: d.id, score };
  }
  return best && best.score >= 0.5 ? best.id : null;
}
